/**
 * ManufacturerCartElement – Web Component reactivo para el carrito por fabricante.
 * Sustituye a ManufacturerCartRenderer con un custom element que se re-renderiza
 * completamente ante cualquier cambio de datos, eliminando los bugs de estado stale.
 */
class ManufacturerCartElement extends HTMLElement {
  constructor() {
    super();
    this._cart = null;
    this._initialized = false;
    this._boundHandlers = {
      cartRefresh: () => this._refreshAndRender(),
      lineItemChange: () => this._refreshAndRender(),
    };
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  connectedCallback() {
    // Re-bind each time the element enters the DOM (handles preserve/restore by cart.js)
    document.removeEventListener('cart:refresh', this._boundHandlers.cartRefresh);
    document.removeEventListener('line-item:change:end', this._boundHandlers.lineItemChange);
    document.addEventListener('cart:refresh', this._boundHandlers.cartRefresh);
    document.addEventListener('line-item:change:end', this._boundHandlers.lineItemChange);

    if (!this._initialized) {
      this._initialized = true;
      this._initFromData();
    }
    // If already initialized, fresh data arrives via line-item:change:end
  }

  disconnectedCallback() {
    document.removeEventListener('cart:refresh', this._boundHandlers.cartRefresh);
    document.removeEventListener('line-item:change:end', this._boundHandlers.lineItemChange);
  }

  // ─── Initialisation ─────────────────────────────────────────────────────────

  async _initFromData() {
    // window.cartItemsData is populated by the inline Liquid script before this runs
    if (window.cartItemsData && window.cartItemsData.length > 0) {
      this._cart = { items: window.cartItemsData };
    } else {
      const attr = this.getAttribute('data-cart-items');
      if (attr) {
        try { this._cart = { items: JSON.parse(attr) }; } catch (e) { /* ignore */ }
      }
    }
    if (this._cart && this._cart.items) {
      this._addLineNumbers(this._cart.items);
      await this._render();
    }
  }

  _addLineNumbers(items) {
    items.forEach((item, index) => {
      item.index = index;
      item.line = index + 1;
    });
  }

  // ─── Data fetching ──────────────────────────────────────────────────────────

  async _refreshAndRender() {
    await this._fetchCartData();
    await this._render();
  }

  async _fetchCartData() {
    try {
      const resp = await fetch(`${window.location.pathname}?sections=main-cart`);
      const data = await resp.json();
      if (data['main-cart']) {
        const tmp = document.createElement('div');
        tmp.innerHTML = data['main-cart'];
        for (const script of tmp.querySelectorAll('script')) {
          if (script.textContent && script.textContent.includes('window.cartItemsData')) {
            try { new Function(script.textContent)(); } catch (e) { /* ignore */ }
          }
        }
      }
      if (window.cartItemsData && window.cartItemsData.length > 0) {
        this._cart = { items: window.cartItemsData };
      } else {
        const cartResp = await fetch('/cart.js');
        this._cart = await cartResp.json();
      }
      if (this._cart && this._cart.items) {
        this._addLineNumbers(this._cart.items);
      }
    } catch (e) {
      console.error('[ManufacturerCart] Error fetching cart data:', e);
    }
  }

  // ─── Rendering ──────────────────────────────────────────────────────────────

  async _render() {
    try {
      if (!this._cart || !this._cart.items || this._cart.items.length === 0) {
        this.innerHTML = '';
        await this._updateCheckoutState();
        return;
      }

      const mCart = new ManufacturerCart();
      const progress = await mCart.calculateProgress(this._cart.items);
      const { groups, noManufacturer } = mCart.groupByManufacturer(this._cart.items);

      let html = '';
      for (const [manufacturerId, group] of groups) {
        const mfData = progress[manufacturerId];
        if (!mfData) continue;
        const { manufacturer } = group;
        const nombre = manufacturer.nombre?.value || manufacturer.nombre || this._t('unknown_manufacturer');
        const foto = manufacturer.foto?.value || manufacturer.foto || '';
        const url = manufacturer.url_fabricante?.value || manufacturer.url_fabricante || '';
        html += this._renderManufacturerGroup(manufacturerId, nombre, foto, url, group.items, mfData.progress);
      }
      if (noManufacturer.length > 0) {
        html += this._renderNoManufacturerGroup(noManufacturer);
      }

      this.innerHTML = html;
      this._setupInteractions();
      await this._updateCheckoutState();
    } catch (e) {
      console.error('[ManufacturerCart] Render error:', e);
    } finally {
      this._setControlsEnabled(true);
    }
  }

  // ─── HTML builders ──────────────────────────────────────────────────────────

  _renderManufacturerGroup(manufacturerId, nombre, foto, urlFabricante, items, progressData) {
    const headerContent = (foto ? `<img src="${foto}" alt="${nombre}" class="manufacturer-logo">` : '')
      + `<h3 class="manufacturer-name">${nombre}</h3>`;
    const headerHtml = urlFabricante
      ? `<a href="${urlFabricante}" class="manufacturer-link">${headerContent}</a>`
      : headerContent;

    let html = `
      <div class="manufacturer-group" data-manufacturer-id="${manufacturerId}">
        <div class="manufacturer-box">
        <div class="manufacturer-group-header">${headerHtml}</div>
    `;

    if (progressData.type === 1 && Array.isArray(progressData.containers)) {
      for (const container of progressData.containers) {
        html += this._renderContainerBlock(container);
      }
    } else {
      html += this._renderSingleProgressBlock(progressData);
    }

    html += `</div>`;

    // Product progress map for Type 3 / 4
    const ppMap = {};
    if (progressData.type === 3 && progressData.productProgress) {
      for (const p of progressData.productProgress) ppMap[p.productId] = p;
    } else if (progressData.type === 4 && progressData.unitsProgress?.productProgress) {
      for (const p of progressData.unitsProgress.productProgress) ppMap[p.productId] = p;
    }

    html += `
      <div class="products-box">
      <form action="${theme.routes.cart_url}" method="post" id="cart-form-${manufacturerId}" novalidate>
        <table class="cart-items no-borders">
          <thead>
            <th class="product-name">${this._t('sections.cart.headings.product')}</th>
            <th class="product-price">${this._t('sections.cart.headings.price')}</th>
            <th class="product-quantity">${this._t('sections.cart.headings.quantity')}</th>
            <th class="product-total">${this._t('sections.cart.headings.total')}</th>
          </thead>
          <tbody>
    `;
    for (const item of items) html += this._renderCartItem(item, ppMap[item.product_id] || null);
    html += `</tbody></table>
      <div class="manufacturer-group-footer">
        <a class="continue-shopping-btn" href="${urlFabricante || '/collections'}">${this._t('continue_shopping')}</a>
      </div>
    </form></div></div>`;
    return html;
  }

  _containerMarkerIcon() {
    return `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M2 8.5 12 4l10 4.5M2 8.5v7L12 20m-10-11.5L12 13m10-4.5v7L12 20m10-11.5L12 13m0 0v7" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M8 10.5v5M16 10.5v5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;
  }

  _renderContainerBlock(container) {
    const pct = Math.round(container.percentage || 0);
    const isValid = container.valid || false;
    const isComplete = isValid && pct >= 100;

    if (isComplete) {
      return `
        <div class="container-progress container-complete" data-container-index="${container.containerIndex}">
          <div class="container-header">
            <div class="container-header-left">
              <h4 class="container-title">${this._t('container')} ${container.containerIndex}</h4>
              <span class="progress-status valid">
                <span class="progress-status-icon valid"></span>
                ${this._t('complete')}
              </span>
            </div>
            <a class="share-container-btn is-disabled" href="/pages/contenedores-compartidos" aria-disabled="true">${this._t('share_container')}</a>
          </div>
          <div class="manufacturer-progress">
            <p class="container-missing-msg">${this._t('congrats_message_html')}</p>
            <div class="progress-bar-container">
              <div class="progress-bar-fill complete" style="width: 100%"></div>
              <span class="progress-marker" style="left: 100%">${this._containerMarkerIcon()}</span>
            </div>
            ${this._renderProgressDetails(container)}
          </div>
        </div>
      `;
    }

    const barClass = isValid ? 'complete' : pct >= 50 ? 'warning' : 'error';
    const statusClass = isValid ? 'valid' : 'invalid';
    return `
      <div class="container-progress${barClass === 'warning' ? ' is-warning' : ''}" data-container-index="${container.containerIndex}">
        <div class="container-header">
          <div class="container-header-left">
            <h4 class="container-title">${this._t('container')} ${container.containerIndex}</h4>
            <span class="progress-status ${statusClass}">
              <span class="progress-status-icon ${statusClass}"></span>
              ${isValid ? this._t('complete') : this._t('incomplete')}
            </span>
          </div>
          <a class="share-container-btn" href="/pages/contenedores-compartidos">${this._t('share_container')}</a>
        </div>
        <div class="manufacturer-progress">
          <p class="container-missing-msg">${this._t('missing_message_html', { percent: 100 - pct })}</p>
          <div class="progress-bar-container">
            <div class="progress-bar-fill ${barClass}" style="width: ${pct}%"></div>
            <span class="progress-marker" style="left: ${pct}%">${this._containerMarkerIcon()}</span>
          </div>
          <div class="progress-info">
            <span class="progress-percentage">${pct}%</span>
          </div>
          ${this._renderProgressDetails(container)}
        </div>
      </div>
    `;
  }

  _renderSingleProgressBlock(progressData) {
    const pct = Math.round(progressData.percentage || 0);
    const isValid = progressData.valid || false;
    const barClass = isValid ? 'complete' : pct >= 50 ? 'warning' : 'error';
    const statusClass = isValid ? 'valid' : 'invalid';
    return `
      <div class="manufacturer-progress manufacturer-progress--single${barClass === 'warning' ? ' is-warning' : ''}">
        <div class="container-header">
          <div class="container-header-left">
            <h4 class="container-title">${this._t('container')} 1</h4>
            <span class="progress-status ${statusClass}">
              <span class="progress-status-icon ${statusClass}"></span>
              ${isValid ? this._t('complete') : this._t('incomplete')}
            </span>
          </div>
          <a class="share-container-btn${isValid ? ' is-disabled' : ''}" href="/pages/contenedores-compartidos">${this._t('share_container')}</a>
        </div>
        <p class="container-missing-msg">${
          isValid
            ? this._t('congrats_message_html')
            : this._t('missing_message_html', { percent: 100 - pct })
        }</p>
        <div class="progress-bar-container">
          <div class="progress-bar-fill ${barClass}" style="width: ${pct}%"></div>
          <span class="progress-marker" style="left: ${pct}%">${this._containerMarkerIcon()}</span>
        </div>
        <div class="progress-info">
          <span class="progress-percentage">${pct}%</span>
        </div>
        ${this._renderProgressDetails(progressData)}
      </div>
    `;
  }

  _renderProgressDetails(progressData) {
    if (!progressData) return '';
    switch (progressData.type) {
      case 1:
        return `
          <div class="type1-details">
            <div>${this._t('volume')}: ${progressData.current.toFixed(2)} m³ / ${progressData.target.toFixed(2)} m³</div>
            <div class="weight-info">${this._t('weight')}: ${progressData.totalWeight.toFixed(0)} kg / ${progressData.maxWeight.toFixed(0)} kg (${Math.round(progressData.weightPercent)}%)</div>
            ${progressData.weightWarning ? `<div class="manufacturer-validation-message weight-warning">${this._t('weight_warning_70_percent')}</div>` : ''}
            ${progressData.isInvalidContainer ? `<div class="manufacturer-validation-message error">${this._t('invalid_container_weight_max_volume_min')}</div>` : ''}
            ${!progressData.valid && !progressData.isInvalidContainer ? `<div class="manufacturer-validation-message error">${this._t('minimum_volume_requirement_not_met')}</div>` : ''}
          </div>
        `;
      case 2:
        return `
          <div class="type2-details">
            <div>${this._t('price')}: ${this._formatMoney(progressData.current)} / ${this._formatMoney(progressData.target)}</div>
            ${!progressData.valid ? `<div class="manufacturer-validation-message error">${this._t('need_more', { amount: this._formatMoney(progressData.target - progressData.current) })}</div>` : ''}
          </div>
        `;
      case 3:
        return `<div class="type3-details"><div class="type3-summary">${this._t('individual_product_progress_shown')}</div></div>`;
      case 4:
        return `
          <div class="type4-details">
            <div class="price-progress">
              <div>${this._t('price')}: ${this._formatMoney(progressData.priceProgress.current)} / ${this._formatMoney(progressData.priceProgress.target)}</div>
              ${!progressData.priceProgress.valid ? `<div class="manufacturer-validation-message error">${this._t('need_more', { amount: this._formatMoney(progressData.priceProgress.target - progressData.priceProgress.current) })}</div>` : ''}
            </div>
            <div class="units-progress-summary">
              <div>${this._t('individual_product_progress_shown')}</div>
            </div>
            ${!progressData.valid ? `<div class="manufacturer-validation-message error">${this._t('both_requirements_must_be_met')}</div>` : ''}
          </div>
        `;
      default:
        return '';
    }
  }

  _renderNoManufacturerGroup(items) {
    let html = `
      <div class="manufacturer-group" data-manufacturer-id="none">
        <div class="products-box">
        <div class="manufacturer-group-header">
          <h3 class="manufacturer-name">${this._t('other_items')}</h3>
        </div>
        <form action="${theme.routes.cart_url}" method="post" id="cart-form-none" novalidate>
          <table class="cart-items no-borders">
            <thead>
              <th class="product-name">${this._t('sections.cart.headings.product')}</th>
              <th class="product-price">${this._t('sections.cart.headings.price')}</th>
              <th class="product-quantity">${this._t('sections.cart.headings.quantity')}</th>
              <th class="product-total">${this._t('sections.cart.headings.total')}</th>
            </thead>
            <tbody>
    `;
    for (const item of items) html += this._renderCartItem(item);
    html += `</tbody></table></form></div></div>`;
    return html;
  }

  _renderCartItem(item, productProgress = null) {
    const imageUrl = item.image || '';
    const productTitle = item.product_title || item.title || '';
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    const quantity = item.quantity || 1;
    const nDePiezasPorSet = parseFloat(item.product?.metafields?.custom?.n_de_piezas_por_set?.value || 0) || 0;
    const totalPiezas = nDePiezasPorSet > 0 ? nDePiezasPorSet * quantity : 0;
    const linePrice = item.final_line_price || item.line_price || 0;
    const originalLinePrice = item.original_line_price || linePrice;
    const hasDiscount = originalLinePrice !== linePrice;
    const itemKey = (item.key || '').replace(/:/g, '_');
    const lineNumber = item.line || (item.index !== undefined ? item.index + 1 : 1);

    let ppHtml = '';
    if (productProgress && productProgress.minUnits > 0) {
      const pp = Math.round(productProgress.percentage || 0);
      const ppClass = productProgress.valid ? 'complete' : pp >= 50 ? 'warning' : 'error';
      const ppStatus = productProgress.valid ? 'valid' : 'invalid';
      ppHtml = `
        <div class="product-row-progress">
          <div class="product-progress-bar-container">
            <div class="product-progress-bar-fill ${ppClass}" style="width: ${pp}%"></div>
          </div>
          <div class="product-progress-info">
            <span class="product-progress-text">${productProgress.quantity} / ${productProgress.minUnits} ${this._t('units')}</span>
            <span class="product-progress-percentage">${pp}%</span>
            <span class="product-progress-status ${ppStatus}">
              <span class="progress-status-icon ${ppStatus}"></span>
              ${productProgress.valid ? this._t('complete') : this._t('incomplete')}
            </span>
          </div>
        </div>
      `;
    }

    return `
      <tr id="CartItem-${lineNumber}" class="cart-item-row"
          data-product-id="${item.product_id}"
          data-variant-id="${item.variant_id}"
          data-line-number="${lineNumber}">
        <td class="product-name">
          <div class="product-cart-item">
            <div class="product-cart-item-image">
              ${imageUrl ? `<img src="${imageUrl}" alt="${productTitle}" loading="lazy">` : ''}
            </div>
            <div>
              <a href="${item.url || '#'}" class="cart-product-link">${productTitle}</a>
              ${variantTitle ? `<div class="product-cart-item-options">${variantTitle}</div>` : ''}
              ${ppHtml}
              <div class="set-number-x-quantity" data-n-de-piezas-por-set="${nDePiezasPorSet}">
                ${totalPiezas > 0 ? `${this._t('sets_totales')}${totalPiezas}` : ''}
              </div>
            </div>
          </div>
        </td>
        <td class="product-price">
          <span class="price">${this._formatMoney(item.final_price || item.price || 0)}</span>
        </td>
        <td class="product-quantity">
          <div class="quantity buttons_added">
            <button class="minus" type="button"
                    aria-label="${this._t('decrease_quantity')}"
                    data-line="${lineNumber}">-</button>
            <input type="number" id="quantity_${itemKey}"
                   class="qty custom-qty" value="${quantity}"
                   data-line="${lineNumber}" data-index="${lineNumber}" min="1">
            <button class="plus" type="button"
                    aria-label="${this._t('increase_quantity')}"
                    data-line="${lineNumber}">+</button>
          </div>
        </td>
        <td class="product-subtotal">
          <div class="product-subtotal-wrapper">
            <span class="price">
              ${hasDiscount ? `<del>${this._formatMoney(originalLinePrice)}</del>` : ''}
              <ins>${this._formatMoney(linePrice)}</ins>
            </span>
            <a href="#" class="remove custom-remove"
               data-line="${lineNumber}" data-index="${lineNumber}"
               aria-label="Remove item">×</a>
          </div>
        </td>
      </tr>
    `;
  }

  // ─── Interactions ────────────────────────────────────────────────────────────

  _setupInteractions() {
    // Quantity +/- buttons
    this.querySelectorAll('.quantity .minus, .quantity .plus').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const input = btn.parentElement.querySelector('input[type="number"]');
        if (!input) return;
        this._setControlsEnabled(false);
        const lineNumber = parseInt(btn.dataset.line || input.dataset.line || input.dataset.index);
        if (!lineNumber) { this._setControlsEnabled(true); return; }
        const current = parseInt(input.value) || 1;
        const newVal = btn.classList.contains('plus') ? current + 1 : Math.max(1, current - 1);
        if (newVal !== current) {
          this._updateQuantity(lineNumber, newVal);
        } else {
          this._setControlsEnabled(true);
        }
      });
    });

    // Remove buttons
    this.querySelectorAll('.remove.custom-remove').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const lineNumber = parseInt(btn.dataset.line || btn.dataset.index);
        if (!lineNumber) return;
        this._updateQuantity(lineNumber, 0);
      });
    });

    // Set-number display update
    this.querySelectorAll('.quantity input[type="number"]').forEach(input => {
      input.addEventListener('input', () => this._updateSetDisplay(input));
      input.addEventListener('change', () => this._updateSetDisplay(input));
    });
  }

  _updateSetDisplay(input) {
    const row = input.closest('tr.cart-item-row');
    if (!row) return;
    const el = row.querySelector('.set-number-x-quantity');
    if (!el) return;
    const nDePiezas = parseFloat(el.dataset.nDePiezasPorSet || '0') || 0;
    const qty = parseInt(input.value, 10) || 0;
    const total = nDePiezas > 0 ? nDePiezas * qty : 0;
    const label = (typeof theme !== 'undefined' && theme.manufacturerCart?.sets_totales)
      ? theme.manufacturerCart.sets_totales : 'Total sets: ';
    el.textContent = total > 0 ? `${label}${total}` : '';
  }

  async _updateQuantity(lineNumber, quantity) {
    if (quantity !== 0 && typeof ManufacturerCart !== 'undefined') {
      const mCart = new ManufacturerCart();
      const validation = await mCart.validateQuantityUpdate(lineNumber, quantity);
      if (!validation.valid) {
        alert(validation.message || 'Cannot update quantity');
        this._setControlsEnabled(true);
        return;
      }
    }

    // Delegate to the Cart class which handles the Shopify API call
    if (!window.customCart && typeof Cart !== 'undefined') {
      const cartContainer = document.getElementById('Cart');
      if (cartContainer) window.customCart = new Cart();
    }
    const cartInstance = window.customCart || window.globalCart;
    if (cartInstance) {
      cartInstance.updateQuantity(lineNumber, quantity);
    } else {
      console.error('[ManufacturerCart] Cart instance not available');
      this._setControlsEnabled(true);
    }
  }

  _setControlsEnabled(enabled) {
    this.querySelectorAll(
      '.quantity .minus, .quantity .plus, .quantity input[type="number"], .remove.custom-remove'
    ).forEach(ctrl => {
      if (enabled) {
        ctrl.removeAttribute('disabled');
        ctrl.style.pointerEvents = '';
        ctrl.style.opacity = '';
      } else {
        ctrl.setAttribute('disabled', 'disabled');
        ctrl.style.pointerEvents = 'none';
        ctrl.style.opacity = '0.6';
      }
    });
  }

  // ─── Checkout state ──────────────────────────────────────────────────────────

  async _updateCheckoutState() {
    const checkoutButton = document.getElementById('checkout-button');
    const blockedMessage = document.getElementById('checkout-blocked-message');
    const blockedList = document.getElementById('checkout-blocked-list');
    if (!checkoutButton) return;

    if (!this._cart || !this._cart.items || this._cart.items.length === 0) {
      checkoutButton.classList.remove('disabled');
      checkoutButton.style.pointerEvents = '';
      checkoutButton.href = '/checkout';
      if (blockedMessage) blockedMessage.style.display = 'none';
      return;
    }

    const mCart = new ManufacturerCart();
    const status = await mCart.isCheckoutAllowed(this._cart.items);

    if (!status.allowed) {
      checkoutButton.classList.add('disabled');
      checkoutButton.style.pointerEvents = 'none';
      checkoutButton.href = '#';
      if (blockedMessage && blockedList) {
        blockedMessage.style.display = 'block';
        blockedList.innerHTML = '';
        for (const invalid of status.invalidManufacturers) {
          const li = document.createElement('li');
          li.textContent = `${invalid.name}: ${this._t('requirements_not_met')}`;
          blockedList.appendChild(li);
        }
      }
    } else {
      checkoutButton.classList.remove('disabled');
      checkoutButton.style.pointerEvents = '';
      checkoutButton.href = '/checkout';
      if (blockedMessage) blockedMessage.style.display = 'none';
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  _formatMoney(cents) {
    if (typeof theme !== 'undefined' && theme.settings?.money_with_currency_format) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    }
    return (cents / 100).toFixed(2) + ' €';
  }

  _t(key, params = {}) {
    // Cart headings
    if (key === 'sections.cart.headings.product') return (typeof theme !== 'undefined' && theme.cartHeadings?.product) || 'Product';
    if (key === 'sections.cart.headings.quantity') return (typeof theme !== 'undefined' && theme.cartHeadings?.quantity) || 'Quantity';
    if (key === 'sections.cart.headings.quantity_extended') return (typeof theme !== 'undefined' && theme.cartHeadings?.quantity_extended) || 'Pallets/boxes quantity';
    if (key === 'sections.cart.headings.price') return (typeof theme !== 'undefined' && theme.cartHeadings?.price) || 'Price';
    if (key === 'sections.cart.headings.total') return (typeof theme !== 'undefined' && theme.cartHeadings?.total) || 'Total';

    if (typeof theme !== 'undefined' && theme.manufacturerCart) {
      let translation = theme.manufacturerCart[key] || '';
      if (params && Object.keys(params).length > 0) {
        Object.keys(params).forEach(param => {
          translation = translation.replace(new RegExp(`{{\\s*${param}\\s*}}`, 'g'), params[param]);
        });
      }
      return translation || key;
    }
    return key;
  }
}

if (!customElements.get('manufacturer-cart')) {
  customElements.define('manufacturer-cart', ManufacturerCartElement);
}

window.ManufacturerCartElement = ManufacturerCartElement;

